/**
 * Group — the cast (REDESIGN.md §7).
 *
 * One row per person: a coloured initial, a name, and a bio written like a note
 * on a character rather than a CV line. Closed until the scenario opens it, and
 * while closed the server sends no names at all, so there is nothing here to
 * reveal early.
 *
 * The island code stays at the foot, quieter than it was: it is real content on
 * the retreat and dropping it would drop the feature, but it is not the point of
 * the screen either.
 */

import { emptyState } from '../components/empty.js'
import { icon } from '../js/icons.js'
import { text } from '../js/html.js'

/**
 * Avatar colours, picked from the name so the same person is the same colour on
 * every device and after every reload. The palette is the reference's.
 */
const AVATAR_COLOURS = ['#4fb3aa', '#5e9ece', '#c98b5e', '#8b7cb8', '#5eb382', '#0e8f8c']

function colourFor(name) {
  let hash = 0

  for (const character of String(name)) {
    hash = (hash * 31 + character.codePointAt(0)) % 9973
  }

  return AVATAR_COLOURS[hash % AVATAR_COLOURS.length]
}

export function renderGroup({ group }) {
  if (group === null) {
    return `<p class="lede">Loading…</p>`
  }

  if (!group.unlocked) {
    return `
      <p class="eyebrow">The cast</p>
      <h1 class="h1">Not introduced yet</h1>
      ${emptyState({
        icon: 'lock',
        title: 'Profiles are locked',
        body: 'They open about ten days before departure. You are not the only one.',
      })}
    `
  }

  const members = group.members ?? []
  const staff = group.staff ?? []

  return `
    <p class="eyebrow">The cast</p>
    <h1 class="h1">${members.length} ${members.length === 1 ? 'traveller' : 'travellers'}</h1>
    <p class="sub">You will know them better than colleagues of ten years.</p>

    ${staff.map((person) => personCard(person, true)).join('')}
    ${members.map((person) => personCard(person, false)).join('')}

    ${islandRule(group.code)}
  `
}

function personCard(person, isHost) {
  const colour = isHost ? '#0e2426' : colourFor(person.name)

  return `
    <div class="pcard ${person.is_you ? 'pcard--you' : ''}">
      <span class="pava" style="background:${colour}">
        ${
          person.photo_url
            ? `<img src="${text(person.photo_url)}" alt="" loading="lazy" />`
            : text(person.initials)
        }
      </span>
      <div class="pcard__body">
        <p class="pname">
          ${text(person.name)}
          ${isHost ? '<span class="pname__tag">Host</span>' : ''}
          ${person.is_you && !isHost ? '<span class="pname__tag">You</span>' : ''}
        </p>
        <p class="pbio">${text(bioLine(person))}</p>
      </div>
    </div>
  `
}

/** Title, city and bio read as one note rather than three fields. */
function bioLine(person) {
  const parts = [person.title, person.city].filter(Boolean).join(', ')
  const bio = person.bio ?? ''

  if (parts === '') return bio || 'No description yet.'
  if (bio === '') return parts

  return `${parts}. ${bio}`
}

function islandRule(code) {
  if (!code?.html) {
    return ''
  }

  return `
    <section class="rule">
      <p class="rule__tag">${icon('moon', { size: 13 })}<span>${text(code.title ?? 'Rule of the island')}</span></p>
      <div class="prose rule__body" id="code-lede">${code.html}</div>
      <div class="rule__foot">
        <span class="rule__vow">${icon('lock', { size: 13 })}<span>Vow of confidentiality active</span></span>
        <button class="rule__more" id="code-toggle" aria-expanded="false" aria-controls="code-lede">
          Read the code
        </button>
      </div>
    </section>
  `
}

/**
 * Collapses the code to its first block until asked. Done here rather than in
 * CSS alone because the button's label and state have to agree with it.
 */
export function mountGroup(root) {
  const button = root.querySelector('#code-toggle')
  const body = root.querySelector('#code-lede')

  if (button === null || body === null) {
    return
  }

  // Nothing to expand when the code is a single paragraph.
  if (body.children.length < 2) {
    button.remove()

    return
  }

  body.classList.add('rule__body--clipped')

  button.addEventListener('click', () => {
    const open = body.classList.toggle('rule__body--clipped') === false

    button.textContent = open ? 'Hide the code' : 'Read the code'
    button.setAttribute('aria-expanded', String(open))
  })
}
